"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import WishlistToggleButton from "@/src/features/wishlist/components/WishlistToggleButton";

type ProductGalleryProps = {
  title: string;
  categoryLabel: string;
  image: string;
  gallery: string[];
  variantId: number;
};

export default function ProductGallery({
  title,
  categoryLabel,
  image,
  gallery,
  variantId,
}: ProductGalleryProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState<number | null>(
    null,
  );
  const transitionTimeoutRef = useRef<number | null>(null);
  const images = gallery.length > 0 ? gallery : [image];

  const goToImage = (nextIndex: number) => {
    if (
      nextIndex === activeImageIndex ||
      nextIndex < 0 ||
      nextIndex >= images.length
    ) {
      return;
    }

    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }

    setPreviousImageIndex(activeImageIndex);
    setActiveImageIndex(nextIndex);

    transitionTimeoutRef.current = window.setTimeout(() => {
      setPreviousImageIndex(null);
      transitionTimeoutRef.current = null;
    }, 780);
  };

  useEffect(() => {
    if (images.length <= 1) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveImageIndex((current) => {
        const nextIndex = (current + 1) % images.length;
        setPreviousImageIndex(current);

        if (transitionTimeoutRef.current) {
          window.clearTimeout(transitionTimeoutRef.current);
        }

        transitionTimeoutRef.current = window.setTimeout(() => {
          setPreviousImageIndex(null);
          transitionTimeoutRef.current = null;
        }, 780);

        return nextIndex;
      });
    }, 4500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [images.length]);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const activeImage = images[activeImageIndex] ?? image;
  const previousImage =
    previousImageIndex !== null ? (images[previousImageIndex] ?? image) : null;
  const thumbnailWindowSize = 4;
  const thumbnailStartIndex =
    images.length <= thumbnailWindowSize
      ? 0
      : Math.min(
          Math.max(activeImageIndex - (thumbnailWindowSize - 1), 0),
          images.length - thumbnailWindowSize,
        );
  const visibleThumbnails = images
    .slice(thumbnailStartIndex, thumbnailStartIndex + thumbnailWindowSize)
    .map((thumbnail, offset) => ({
      thumbnail,
      index: thumbnailStartIndex + offset,
    }));
  const imageFrameClassName = "relative aspect-[16/9] w-full min-w-0 min-h-[195px] overflow-hidden rounded-[1rem] bg-[#f9f2ec] md:min-h-[220px] md:rounded-[1.8rem] lg:min-h-[320px]";

  return (
    <div className="w-full min-w-0 space-y-4">
      <div className="relative w-full min-w-0 overflow-hidden rounded-[1.75rem] border border-[rgba(238,55,106,0.1)] bg-white p-3 shadow-[0_18px_34px_rgba(61,45,33,0.06)] md:rounded-[2.2rem] md:border-[rgba(238,55,106,0.14)] md:p-3 md:shadow-[0_24px_54px_rgba(61,45,33,0.08)]">
        <div className="absolute left-5 top-5 z-20">
          <WishlistToggleButton
            variantId={variantId}
            title={title}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(238,55,106,0.18)] bg-white text-[#ee376a] shadow-[0_14px_30px_rgba(61,45,33,0.08)] transition hover:bg-[#fff3f8]"
            filledClassName="border-[#ee376a] bg-[#ee376a] !text-white hover:border-[#ee376a] hover:bg-[#ee376a] hover:!text-white"
          />
        </div>

        <div className={imageFrameClassName}>
          {previousImage ? (
            <Image
              src={previousImage}
              alt={`${title} من ${categoryLabel} لدى YUMA`}
              fill
              sizes="(min-width: 1024px) 52vw, 100vw"
              className="pointer-events-none object-contain object-center"
            />
          ) : null}
          <Image
            key={activeImage}
            src={activeImage}
            alt={`${title} من ${categoryLabel} لدى YUMA`}
            fill
            preload
            fetchPriority="high"
            sizes="(min-width: 1024px) 52vw, 100vw"
            className="object-contain object-center"
          />
        </div>

        {images.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() =>
                goToImage(
                  activeImageIndex === 0 ? images.length - 1 : activeImageIndex - 1,
                )
              }
              className="absolute right-4 top-1/2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(238,55,106,0.16)] bg-white text-[var(--color-ink)] shadow-[0_14px_30px_rgba(61,45,33,0.12)] transition hover:bg-[#fff3f8] md:right-6 md:h-11 md:w-11"
              aria-label="الصورة السابقة"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => goToImage((activeImageIndex + 1) % images.length)}
              className="absolute left-4 top-1/2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(238,55,106,0.16)] bg-white text-[var(--color-ink)] shadow-[0_14px_30px_rgba(61,45,33,0.12)] transition hover:bg-[#fff3f8] md:left-6 md:h-11 md:w-11"
              aria-label="الصورة التالية"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </>
        ) : null}
      </div>

      {images.length > 1 ? (
        <div
          className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))_auto] items-center gap-2 md:gap-3"
          data-reveal-group
        >
          <button
            type="button"
            onClick={() =>
              goToImage(
                activeImageIndex === 0 ? images.length - 1 : activeImageIndex - 1,
              )
            }
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(238,55,106,0.16)] bg-white text-[var(--color-ink)] shadow-[0_10px_24px_rgba(61,45,33,0.08)] transition hover:bg-[#fff3f8]"
            aria-label="السابق"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {visibleThumbnails.map(({ thumbnail, index }) => {
            const isActive = index === activeImageIndex;

            return (
              <button
                key={`${thumbnail}-${index}`}
                type="button"
                onClick={() => goToImage(index)}
                className={`relative overflow-hidden rounded-[1rem] border p-1.5 transition-all md:rounded-[1.2rem] md:p-2 ${
                  isActive
                    ? "border-[#ee376a] bg-white shadow-[0_16px_32px_rgba(61,45,33,0.12)]"
                    : "border-[rgba(238,55,106,0.16)] bg-white"
                }`}
                aria-label={`عرض الصورة ${index + 1}`}
              >
                <div className="relative aspect-square overflow-hidden rounded-[0.8rem] bg-[#f9f2ec] md:rounded-[1rem]">
                  <Image
                    src={thumbnail}
                    alt={`${title} - صورة ${index + 1} من YUMA`}
                    fill
                    sizes="160px"
                    className="object-cover"
                  />
                </div>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => goToImage((activeImageIndex + 1) % images.length)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(238,55,106,0.16)] bg-white text-[var(--color-ink)] shadow-[0_10px_24px_rgba(61,45,33,0.08)] transition hover:bg-[#fff3f8]"
            aria-label="التالي"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
